import type { ReactNode } from 'react';

import { cn } from '../../shared';
import { OverlayScrollArea } from '../../wrappers';
import { Sidebar, type SidebarConfig } from '../sidebar/sidebar';
import { HEADER_HEIGHT, type HeaderSize } from './header-size';

export interface AppShellProps {
  header?: ReactNode;
  headerFixed?: boolean;
  headerSize?: HeaderSize;
  footer?: ReactNode;
  footerFixed?: boolean;
  footerSize?: HeaderSize;
  sidebar?: SidebarConfig;
  mainBanner?: ReactNode;
  className?: string;
  children: ReactNode;
}

export function AppShell({
  header,
  headerFixed = false,
  headerSize = 'md',
  footer,
  footerFixed = false,
  footerSize = 'md',
  sidebar,
  mainBanner,
  className,
  children,
}: AppShellProps) {
  const hasHeader = header !== undefined;
  const hasFooter = footer !== undefined;
  const hasSidebar = sidebar !== undefined;

  // A single-column area string ("header") when there's no sidebar column to
  // span — a two-word string here would work by spanning an implicit second
  // track, but only because nothing has added a region since.
  const span = (name: string) => (hasSidebar ? `"${name} ${name}"` : `"${name}"`);
  const areas = [
    hasHeader && span('header'),
    hasSidebar ? '"sidebar main"' : '"main"',
    hasFooter && span('footer'),
  ]
    .filter(Boolean)
    .join(' ');
  const rows = [hasHeader && 'auto', '1fr', hasFooter && 'auto'].filter(Boolean).join(' ');

  return (
    <div
      // Backstop: a broken min-h-0 downstream should scroll <main>, not grow the shell into the document.
      className={cn(
        'grid h-dvh overflow-hidden',
        hasSidebar ? 'grid-cols-[auto_1fr]' : 'grid-cols-1',
        className,
      )}
      style={{ gridTemplateAreas: areas, gridTemplateRows: rows }}
    >
      {hasHeader ? (
        <div className={cn('[grid-area:header]', headerFixed && HEADER_HEIGHT[headerSize])}>
          {headerFixed ? (
            <>
              <div aria-hidden className={HEADER_HEIGHT[headerSize]} />
              <div className={cn('fixed inset-x-0 top-0 z-fixed', HEADER_HEIGHT[headerSize])}>
                {header}
              </div>
            </>
          ) : (
            header
          )}
        </div>
      ) : null}

      {sidebar ? (
        // Same min-h-0/min-w-0 trap as the content cell: a tall nav list would otherwise grow this row.
        <div className="min-h-0 min-w-0 [grid-area:sidebar]">
          <Sidebar {...sidebar} />
        </div>
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-col [grid-area:main]">
        {mainBanner}
        <main className="flex min-h-0 flex-1 flex-col">
          <OverlayScrollArea orientation="vertical" className="min-h-0 flex-1">
            {children}
          </OverlayScrollArea>
        </main>
      </div>

      {hasFooter ? (
        <div className={cn('[grid-area:footer]', footerFixed && HEADER_HEIGHT[footerSize])}>
          {footerFixed ? (
            <>
              <div aria-hidden className={HEADER_HEIGHT[footerSize]} />
              <div className={cn('fixed inset-x-0 bottom-0 z-fixed', HEADER_HEIGHT[footerSize])}>
                {footer}
              </div>
            </>
          ) : (
            footer
          )}
        </div>
      ) : null}
    </div>
  );
}
